import { Children, Fragment, type ReactNode, isValidElement } from 'react';

import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

/**
 * The toolbar-item grammar. See `docs/design-system/TOOLBAR-DOCTRINE.md`.
 *
 * Callers declare INTENT — which surface class this toolbar belongs to — and
 * this component owns the styling. No caller should ever hand-roll
 * `page-header-action-group` / `header-action-pill` itself.
 *
 * THE SURFACE DISTINCTION IN ONE LINE: `chrome` acts on the PAGE and is always
 * visibly a control; `workspace` acts inside a TOOL and disappears into its
 * surface on desktop. It is a real founder decision (2026-08-12, restated after
 * an intermediate draft argued the opposite and was overruled), not a fork —
 * which is why the prop stays and every caller must obey it rather than
 * hand-rolling a class.
 *
 * - `chrome` (default) — a top-level PAGE header: Discover, Assets, Campaigns,
 *   Briefs. These are PAGE ACTIONS, so they always carry a visible button
 *   ground, and the icon group keeps its capsule at every breakpoint.
 * - `workspace` — a toolbar INSIDE a surface: the unified media dialog, the
 *   post/image/video previewer, the ad & brief studio (Concept Workbench), the
 *   engagement hub. These are TOOL chrome, so the icons run bare on desktop and
 *   cluster only on touch.
 *
 * Both classes already encode that responsive split in `src/index.css`:
 * `page-header-action-group` is a capsule at all widths; `header-action-pill`
 * is a capsule below 768px and goes transparent/borderless at `md:`.
 *
 * Responsive grammar, independent of surface:
 * - Desktop keeps a labeled primary independent from the icon-only group.
 * - Mobile uses the compact/icon-only primary, then fuses it with the overflow
 *   actions when both exist. A lone primary stays standalone so it never
 *   acquires an empty capsule.
 * - A group that would hold exactly ONE control is not a group. It drops the
 *   CAPSULE — and keeps its BUTTON GROUND. Measured on /campaigns 2026-08-23:
 *   the desktop group held only the ⋯ overflow, so the capsule drew a 32px
 *   bordered circle around one icon — which then read as an outlined peer of
 *   the solid `New campaign` pill beside it rather than as a menu affordance. A
 *   capsule's whole job is to say "these belong together"; with one member it
 *   groups nothing and only adds weight.
 *   ⚠️ That fix removed BOTH facts when only the second was wrong, and the
 *   over-correction is what the founder saw on 2026-08-27: `/campaigns` and
 *   `/publishing` rendered a fully transparent ⋯ (`rgba(0,0,0,0)`, `0px`
 *   border) while `/products`, `/discover` and `/insights` — whose headers are
 *   built elsewhere and hand-rolled `.header-nav-btn` — rendered a filled one
 *   (`rgb(28,28,31)`, `1px rgba(39,39,42,.8)`). Ground is now supplied HERE, by
 *   `page-header-action-ground`, for every `chrome` action group at every
 *   count, so no page can be missing it and no caller has to remember a class.
 *   Handled HERE and not in CSS on purpose: `:has(> :only-child)` cannot see
 *   that DataShell's mobile search trigger is a deliberate lone-child capsule,
 *   sized to match the filter capsule sharing its row.
 *
 * ONE LABELLED PRIMARY. `desktopPrimary` is the page's single anchor action;
 * a page that offers a second labelled action passes it as `desktopSecondary`,
 * and everything past that is demoted to an icon or a `⋯` row in
 * `desktopActions`. This is a slot, not a comment, because the fragment form
 * hid the rule: measured on `/briefs/:id/concepts/:id` (2026-08-26) three
 * labelled buttons — `Open design canvas →` 168px, `Select copy to create an
 * ad` 209px, `Save` 80px — were passed as ONE fragment into `desktopPrimary`,
 * none of them `variant="neutral"`, so nothing read as the anchor. The 478px
 * they took came straight out of the `<h1>` beside them (`md:flex-1` title
 * column, `shrink-0` actions), and BOTH breadcrumb segments truncated on a
 * page where every sibling route truncates none. Two slots also fix the order,
 * which used to disagree per page: `/briefs` rendered secondary-then-primary,
 * `/campaigns` primary-then-secondary.
 *
 * `desktopSecondary` is desktop-only on purpose. Mobile has room for one
 * labelled control at most, so a second one belongs in `mobileActions` — where
 * the caller can decide whether it is a row in the `⋯` menu or nothing.
 *
 * The caller owns the actual action semantics. This component only composes the
 * responsive chrome; it must not move or duplicate route behavior.
 */
export type PageHeaderSurface = 'chrome' | 'workspace';

const GROUP_CLASS: Record<PageHeaderSurface, string> = {
  chrome: 'page-header-action-group',
  workspace: 'header-action-pill',
};

/**
 * The BUTTON ground for route-chrome actions, applied to the wrapper so it
 * reaches every member (`src/index.css`, in the same declaration block as
 * `.header-nav-btn` so the two spellings cannot drift). Separate from
 * `GROUP_CLASS` because ground and grouping answer different questions — see
 * the docblock above.
 */
const GROUND_CLASS = 'page-header-action-ground';

/**
 * A header re-renders on every route change, filter, and query settle, so an
 * un-deduped warning would bury the console it is trying to be visible in.
 * Once per message per session is enough to be read and acted on.
 */
const warnedOnce = new Set<string>();

const warnOnce = (message: string): void => {
  if (warnedOnce.has(message)) return;
  warnedOnce.add(message);
  logger.warn(message);
};

export function PageHeaderActionLayout({
  desktopPrimary,
  desktopSecondary,
  mobilePrimary,
  desktopActions,
  mobileActions = desktopActions,
  surface = 'chrome',
}: {
  desktopPrimary?: ReactNode;
  /**
   * A single quiet labelled action rendered before the primary, desktop only.
   * Use `variant="outline"` here and keep `variant="neutral"` for the primary —
   * two filled pills side by side make neither one the anchor.
   */
  desktopSecondary?: ReactNode;
  mobilePrimary?: ReactNode;
  desktopActions?: ReactNode;
  mobileActions?: ReactNode;
  surface?: PageHeaderSurface;
}): JSX.Element {
  const hasDesktopPrimary = desktopPrimary != null;
  const hasDesktopSecondary = desktopSecondary != null;
  const resolvedMobilePrimary = mobilePrimary ?? desktopPrimary;
  const hasMobilePrimary = resolvedMobilePrimary != null;
  const hasDesktopActions = desktopActions != null;
  const hasMobileActions = mobileActions != null;

  const groupClass = cn(GROUP_CLASS[surface], 'inline-flex items-center');

  /**
   * Ground goes on the ACTION group only — never on the fused mobile capsule
   * that holds the primary. `chrome` actions are page controls and always read
   * as controls; `workspace` actions live on a tool surface and stay bare on
   * desktop by design. The primary supplies its own weight (`variant="neutral"`
   * is a solid pill), so painting the sidebar ground under it there would
   * cancel the one control that is supposed to be the anchor.
   */
  const actionGroupClass = cn(groupClass, surface === 'chrome' ? GROUND_CLASS : undefined);

  /**
   * How many controls a group would actually hold.
   *
   * `Children.count` alone is WRONG here: callers pass two actions as a
   * fragment, and a fragment counts as one. Trusting that count silently
   * un-grouped every real pair while every test that rendered two loose
   * children still passed — the repo's recurring silent-no-op shape. Unwrap
   * fragments before counting.
   */
  const countControls = (node: ReactNode): number => {
    const items = Children.toArray(node);
    if (items.length === 1 && isValidElement(items[0]) && items[0].type === Fragment) {
      return countControls((items[0].props as { children?: ReactNode }).children);
    }
    return items.length;
  };

  /**
   * A capsule around a single control groups nothing — so a lone action drops
   * the capsule, keeps the ground, and stays inside the same wrapper. Both
   * branches render the SAME element with the same ground: only the capsule
   * class differs, which is the only fact that actually depends on the count.
   */
  const group = (children: ReactNode) => (
    <div
      className={
        countControls(children) === 1
          ? cn(surface === 'chrome' ? GROUND_CLASS : undefined, 'inline-flex items-center')
          : actionGroupClass
      }
    >
      {children}
    </div>
  );

  /**
   * The one-primary rule, enforced where it can actually be seen.
   *
   * Stated only in prose it was talked past for months — the same lesson as
   * `countControls` itself. A warning (not a throw) because the header renders
   * on every route: a page that trips this should still be usable while it is
   * being corrected, and the message names the fix rather than the symptom.
   */
  if (import.meta.env.DEV) {
    const primaryCount = countControls(desktopPrimary);
    if (primaryCount > 1) {
      warnOnce(
        `PageHeaderActionLayout: desktopPrimary holds ${primaryCount} controls. A page header has ONE labelled primary — pass the second as desktopSecondary and demote the rest to desktopActions (icon-only, or a row in the ⋯ menu). Every labelled pixel here is taken out of the page title beside it.`,
      );
    }
    if (countControls(desktopSecondary) > 1) {
      warnOnce(
        'PageHeaderActionLayout: desktopSecondary holds more than one control. It is a single quiet action; anything further belongs in desktopActions.',
      );
    }
  }

  if (!hasDesktopActions && !hasMobileActions && !hasDesktopSecondary && mobilePrimary == null)
    return <>{desktopPrimary}</>;

  return (
    <>
      <div className="hidden items-center gap-2 md:flex">
        {hasDesktopSecondary ? desktopSecondary : null}
        {hasDesktopPrimary ? desktopPrimary : null}
        {hasDesktopActions ? group(desktopActions) : null}
      </div>

      <div className="flex items-center md:hidden">
        {hasMobilePrimary && hasMobileActions ? (
          <div className={groupClass}>
            {resolvedMobilePrimary}
            {mobileActions}
          </div>
        ) : hasMobilePrimary ? (
          resolvedMobilePrimary
        ) : hasMobileActions ? (
          group(mobileActions)
        ) : null}
      </div>
    </>
  );
}
